import { useEffect, useMemo, useState } from "react";
import { Alert, Form, Table } from "react-bootstrap";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import {
  organizerListEventsApi,
  organizerMarketingAudienceCountApi,
  organizerMarketingListCampaignsApi,
  organizerMarketingSendEmailApi,
  organizerMarketingSummaryApi,
  organizerMarketingUploadImageApi,
  organizerMarketingTestEmailApi,
  organizerUpdateEventApi,
} from "../../api/api";
import { handleError } from "../../helpers/handleError";
import {
  getMetaPixelConfig,
  isMetaPixelScriptLike,
  isValidMetaPixelId,
  loadMetaPixel,
  trackMetaPixel,
} from "../../helpers/metaPixel";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import "../dashboard.css";

const MARKETING_TABS = [
  { key: "pixel", label: "PIXEL" },
  { key: "email", label: "EMAIL" },
  { key: "whatsapp", label: "WHATSAPP" },
  { key: "push", label: "NOTIFICACIONES PUSH" },
];

const AudienceIcon = () => (
  <svg viewBox="0 0 22 22" fill="currentColor" aria-hidden="true">
    <circle cx="11" cy="8" r="4" />
    <path d="M3 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
  </svg>
);

const ContactsIcon = () => (
  <svg viewBox="0 0 24 22" fill="currentColor" aria-hidden="true">
    <circle cx="9" cy="6" r="4.5" />
    <path d="M.5 19.5c0-4.7 3.8-8.5 8.5-8.5s8.5 3.8 8.5 8.5" />
    <path d="M18 13h6M21 10v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const ChevronIcon = () => (
  <svg viewBox="0 0 14 8" fill="none" aria-hidden="true">
    <path
      d="M1 1l6 6 6-6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <path
      d="M4 24s7-12 20-12c4 0 7.5 1 10.5 2.5M44 24s-7 12-20 12c-3.5 0-6.7-.8-9.5-2.2"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    />
    <path
      d="M19 19a7 7 0 0110 10M28 28a7 7 0 01-9-9"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    />
    <path d="M6 6l36 36" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);

const UploadCloudIcon = () => (
  <svg viewBox="0 0 40 40" fill="none" aria-hidden="true">
    <path
      d="M11 28a7 7 0 010-14 9 9 0 0117.5 2A6 6 0 0130 28H11z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M20 32V19m-4 4l4-4 4 4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

const MarketingMetricCard = ({ icon, label, value, foot }) => (
  <div className="organizer-marketing-metric-col">
    <article className="organizer-marketing-metric-card">
      <span className="organizer-marketing-metric-card__icon">{icon}</span>
      <div className="organizer-marketing-metric-card__body">
        <span className="organizer-marketing-metric-card__label">{label}</span>
        <strong className="organizer-marketing-metric-card__value">{value}</strong>
      </div>
    </article>
    <span className="organizer-marketing-metric-card__foot">| {foot}</span>
  </div>
);

const MarketingComingSoon = () => (
  <div className="organizer-marketing-empty">
    <span className="organizer-marketing-empty__icon">
      <EyeOffIcon />
    </span>
    <span className="organizer-marketing-empty__title">PRÓXIMAMENTE...</span>
    <span className="organizer-marketing-empty__hint">Disponible en próxima actualización</span>
  </div>
);

const getCampaignVisualState = (campaign) => {
  const status = String(campaign?.status || "").toLowerCase();
  if (status.includes("success") || status.includes("sent") || status.includes("enviado")) return "success";
  if (status.includes("fail") || status.includes("error") || status.includes("rechaz")) return "danger";
  if (Number(campaign?.sentCount || 0) > 0 && Number(campaign?.failedCount || 0) === 0) return "success";
  if (Number(campaign?.failedCount || 0) > 0) return "danger";
  return "neutral";
};

const getCampaignStatusLabel = (campaign) => {
  const visualState = getCampaignVisualState(campaign);
  if (visualState === "success") return "ENVIADO";
  if (visualState === "danger") return "NO ENVIADO";
  return (campaign?.status || "PENDIENTE").toUpperCase();
};

const formatCampaignDate = (campaign) => {
  if (!campaign?.createdAt) return { date: "-", time: "" };
  const d = new Date(campaign.createdAt);
  if (Number.isNaN(d.getTime())) return { date: "-", time: "" };
  const date = d.toLocaleDateString("es-AR");
  const time = d.toLocaleTimeString("es-AR", { hour12: false });
  return { date, time };
};

const formatNumber = (value) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed)) return "0";
  return parsed.toLocaleString("es-AR");
};

const Marketing = () => {
  const [activeTab, setActiveTab] = useState("pixel");
  const [events, setEvents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [audienceCount, setAudienceCount] = useState(null);
  const [audienceRaw, setAudienceRaw] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [savingPixel, setSavingPixel] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [pixelStatus, setPixelStatus] = useState("");
  const [form, setForm] = useState({
    lastEventId: "",
    subject: "",
    message: "",
    imageUrl: "",
  });
  const [pixelForm, setPixelForm] = useState({
    eventId: "",
    enabled: false,
    pixelId: "",
  });
  const [isDragging, setIsDragging] = useState(false);

  const eventOptions = useMemo(
    () =>
      (events || [])
        .map((ev) => {
          const raw = ev?.id ?? ev?._id ?? ev?.eventid;
          if (!raw) return null;
          return { value: String(raw), label: ev?.name || "Evento sin nombre" };
        })
        .filter(Boolean),
    [events]
  );

  const selectedPixelEvent = useMemo(
    () =>
      (events || []).find((ev) => String(ev?.id || ev?._id || "") === String(pixelForm.eventId || "")) ||
      null,
    [events, pixelForm.eventId]
  );

  const pixelConfigured = isValidMetaPixelId(pixelForm.pixelId);
  const pixelActive = Boolean(pixelForm.enabled && pixelConfigured);
  const historyItems = useMemo(() => (campaigns || []).slice(0, 20), [campaigns]);
  const quotaRemaining = summary?.quota?.remaining ?? null;
  const quotaLimit = summary?.quota?.limit ?? null;

  const readPixelConfig = (ev) => {
    const config = getMetaPixelConfig(ev || {});
    return {
      enabled: Boolean(config.enabled),
      pixelId: config.pixelId || "",
    };
  };

  const load = async () => {
    setLoading(true);
    setError("");
    setStatus("");
    try {
      const [evRes, sumRes, campRes] = await Promise.all([
        organizerListEventsApi(),
        organizerMarketingSummaryApi(),
        organizerMarketingListCampaignsApi(),
      ]);
      const eventRows = Array.isArray(evRes?.data?.info) ? evRes.data.info : [];
      setEvents(eventRows);
      setSummary(sumRes?.data?.info || null);
      setCampaigns(campRes?.data?.info?.items || []);
      if (!pixelForm.eventId && eventRows.length) {
        const first = eventRows[0];
        const pixel = readPixelConfig(first);
        setPixelForm({
          eventId: String(first?.id || first?._id || ""),
          enabled: pixel.enabled,
          pixelId: pixel.pixelId,
        });
      }
      if (!form.lastEventId && eventRows.length) {
        setForm((prev) => ({
          ...prev,
          lastEventId: prev.lastEventId || String(eventRows[0]?.id || eventRows[0]?._id || ""),
        }));
      }
    } catch (e) {
      handleError(e);
      setError(e?.response?.data?.message || "No se pudo cargar la sección de difusión.");
    } finally {
      setLoading(false);
    }
  };

  const refreshAudienceCount = async (eventId) => {
    try {
      setAudienceCount(null);
      setAudienceRaw(null);
      const body = { filter: { lastEventId: eventId || null } };
      const res = await organizerMarketingAudienceCountApi(body);
      const count = res?.data?.info?.count;
      const raw = res?.data?.info?.raw;
      if (typeof count === "number") setAudienceCount(count);
      if (typeof raw === "number") setAudienceRaw(raw);
    } catch (_) {
      setAudienceCount(null);
      setAudienceRaw(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshAudienceCount(form.lastEventId);
  }, [form.lastEventId]);

  useEffect(() => {
    if (!selectedPixelEvent) return;
    const pixel = readPixelConfig(selectedPixelEvent);
    setPixelForm((prev) => ({
      ...prev,
      enabled: pixel.enabled,
      pixelId: pixel.pixelId,
    }));
    setPixelStatus("");
  }, [selectedPixelEvent]);

  const onSend = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("");
    const subject = String(form.subject || "").trim();
    const message = String(form.message || "").trim();
    if (!subject) {
      setError("El asunto es obligatorio.");
      return;
    }
    if (!message) {
      setError("El mensaje es obligatorio.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        subject,
        message,
        imageUrl: form.imageUrl || null,
        filter: { lastEventId: form.lastEventId || null },
      };
      const res = await organizerMarketingSendEmailApi(payload);
      const info = res?.data?.info || {};
      if (info.total === 0) {
        setStatus(info.message || "No hay destinatarios para este segmento.");
      } else {
        const sent = Number(info.sent || 0);
        const failed = Number(info.failed || 0);
        const capped = Boolean(info.capped);
        setStatus(
          `Enviados: ${sent} · Fallidos: ${failed}${capped ? " · Límite aplicado" : ""}${failed ? " (revisá SMTP/logs)" : ""}`
        );
      }
      setForm((prev) => ({
        ...prev,
        subject: "",
        message: "",
      }));
      await load();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "No se pudo enviar la difusión.");
    } finally {
      setLoading(false);
    }
  };

  const uploadImageFile = async (file) => {
    if (!file) return;
    setError("");
    setStatus("");
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await organizerMarketingUploadImageApi(fd);
      const url = res?.data?.info?.url;
      setForm((prev) => ({ ...prev, imageUrl: url ? String(url) : "" }));
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo cargar la imagen.");
    } finally {
      setUploadingImage(false);
    }
  };

  const onPickImage = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    await uploadImageFile(file);
    try {
      if (e?.target) e.target.value = "";
    } catch (_) {}
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loading && !uploadingImage) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (loading || uploadingImage) return;

    const file = e.dataTransfer?.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Solo se permiten archivos de imagen.");
      return;
    }

    await uploadImageFile(file);
  };

  const onTestEmail = async () => {
    setError("");
    setStatus("");
    setLoading(true);
    try {
      const res = await organizerMarketingTestEmailApi();
      const to = res?.data?.info?.to;
      setStatus(`Email de prueba enviado a: ${to || "tu email"}`);
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo enviar el email de prueba.");
    } finally {
      setLoading(false);
    }
  };

  const onPixelEventChange = (eventId) => {
    const selected = (events || []).find((ev) => String(ev?.id || ev?._id || "") === String(eventId || ""));
    const pixel = readPixelConfig(selected);
    setPixelForm({
      eventId: eventId ? String(eventId) : "",
      enabled: pixel.enabled,
      pixelId: pixel.pixelId,
    });
    setPixelStatus("");
  };

  const onPixelIdChange = (value) => {
    if (isMetaPixelScriptLike(value)) {
      setPixelStatus("Ingresá solo el Pixel ID, no scripts HTML o JavaScript.");
      return;
    }
    const digitsOnly = String(value || "").replace(/\D+/g, "").slice(0, 20);
    setPixelForm((prev) => ({ ...prev, pixelId: digitsOnly }));
    setPixelStatus("");
  };

  const onSavePixel = async () => {
    setError("");
    setPixelStatus("");
    if (!pixelForm.eventId) {
      setPixelStatus("Seleccioná un evento.");
      return;
    }
    if (pixelForm.enabled && !pixelForm.pixelId) {
      setPixelStatus("Pixel ID requerido cuando Meta Pixel está activo.");
      return;
    }
    if (pixelForm.pixelId && !pixelConfigured) {
      setPixelStatus("El Pixel ID debe tener entre 10 y 20 números.");
      return;
    }

    setSavingPixel(true);
    try {
      const fd = new FormData();
      fd.append("id", pixelForm.eventId);
      fd.append("marketingMetaPixelEnabled", String(Boolean(pixelForm.enabled)));
      fd.append("marketingMetaPixelPixelId", pixelForm.pixelId || "");
      const res = await organizerUpdateEventApi(fd);
      const saved = res?.data?.info || null;
      setEvents((prev) =>
        (prev || []).map((ev) =>
          String(ev?.id || ev?._id || "") === String(pixelForm.eventId)
            ? {
                ...ev,
                marketing: saved?.marketing || {
                  ...(ev.marketing || {}),
                  metaPixel: {
                    enabled: Boolean(pixelForm.enabled),
                    pixelId: pixelForm.pixelId || "",
                  },
                },
              }
            : ev
        )
      );
      setPixelStatus("Pixel configurado correctamente.");
    } catch (err) {
      setPixelStatus(err?.response?.data?.message || "No se pudo guardar el Pixel.");
    } finally {
      setSavingPixel(false);
    }
  };

  const onTestPixel = () => {
    setPixelStatus("");
    if (!pixelConfigured) {
      setPixelStatus("Guardá un Pixel ID válido para probarlo.");
      return;
    }
    if (loadMetaPixel(pixelForm.pixelId)) {
      trackMetaPixel("PageView");
      setPixelStatus("Pixel activo.");
    } else {
      setPixelStatus("No se pudo activar el Pixel en este navegador.");
    }
  };

  const renderPixelTab = () => (
    <div className="organizer-marketing-pixel-row">
      <div className="organizer-marketing-pixel-col">
        <article className="organizer-marketing-pixel-card">
          <div className="organizer-marketing-pixel-field">
            <OrganizerPillSelect
              placeholder="Selecciona tu evento"
              value={pixelForm.eventId}
              onChange={onPixelEventChange}
              options={eventOptions}
            />
          </div>

          <div className="organizer-marketing-pixel-field">
            <div className="organizer-marketing-pixel-field__copy">
              <span className="organizer-marketing-pixel-field__label">Pixel ID</span>
              <span className="organizer-marketing-pixel-field__hint">| Encontralo en Meta Events Manager</span>
            </div>
            <Form.Control
              className="organizer-marketing-pixel-input"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={20}
              value={pixelForm.pixelId}
              onChange={(e) => onPixelIdChange(e.target.value)}
              onPaste={(e) => {
                const pasted = e.clipboardData?.getData("text") || "";
                if (isMetaPixelScriptLike(pasted)) {
                  e.preventDefault();
                  setPixelStatus("No pegues scripts: ingresá solo el Pixel ID.");
                }
              }}
              placeholder="12345678912345"
            />
          </div>

          <div className="organizer-marketing-pixel-field">
            <div className="organizer-marketing-pixel-field__copy">
              <span className="organizer-marketing-pixel-field__label">Activar Meta Pixel</span>
              <span className="organizer-marketing-pixel-field__hint">| Medición segura por Pixel ID</span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={pixelForm.enabled}
              className={`organizer-marketing-switch ${pixelForm.enabled ? "is-on" : ""}`}
              onClick={() => {
                setPixelForm((prev) => ({ ...prev, enabled: !prev.enabled }));
                setPixelStatus("");
              }}
            />
          </div>

          <div className="organizer-marketing-pixel-actions">
            <button
              type="button"
              className="organizer-marketing-btn organizer-marketing-btn--primary"
              onClick={onSavePixel}
              disabled={savingPixel}
            >
              {savingPixel ? "Guardando..." : "Guardar Pixel"}
            </button>
          </div>
        </article>

        <span className={`organizer-marketing-status-pill ${pixelActive ? "is-active" : ""}`}>
          <span className="dot" />
          {pixelActive ? "Pixel Activo" : "Pixel no disponible"}
        </span>

        {pixelStatus ? (
          <p className="organizer-marketing-pixel-message" aria-live="polite">
            {pixelStatus}
          </p>
        ) : null}
      </div>

      <MarketingMetricCard
        icon={<AudienceIcon />}
        label="AUDIENCIA GENERAL"
        value={formatNumber(summary?.audience?.totalOptedIn)}
        foot="Disponibles para contactar"
      />

      <MarketingMetricCard
        icon={<ContactsIcon />}
        label="NUEVOS CONTACTOS"
        value={formatNumber(summary?.audience?.last30OptedIn)}
        foot="En los últimos 30 días"
      />
    </div>
  );

  const renderEmailTab = () => {
    const bodyLength = String(form.message || "").length;
    const messageMax = 2000;
    const remaining = Math.max(0, messageMax - bodyLength);
    return (
      <>
        <article className="organizer-marketing-card">
          <h2 className="organizer-marketing-section-title">Crear campaña</h2>

          <Form onSubmit={onSend}>
            <div className="organizer-marketing-email-grid">
              <div className="organizer-marketing-email-left">
                <OrganizerPillSelect
                  placeholder="Selecciona tu evento"
                  value={form.lastEventId}
                  onChange={(val) => setForm((prev) => ({ ...prev, lastEventId: val ? String(val) : "" }))}
                  options={eventOptions}
                />

                <Form.Control
                  className="organizer-marketing-pixel-input"
                  placeholder="Asunto"
                  value={form.subject}
                  onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
                  required
                />

                <Form.Control
                  as="textarea"
                  rows={6}
                  className="organizer-marketing-textarea"
                  placeholder="Contenido del email"
                  value={form.message}
                  maxLength={messageMax}
                  onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                  required
                />
              </div>

              <div className="organizer-marketing-image-section">
                <span className="organizer-marketing-image-section__label">Imagen promocional</span>
                <span className="organizer-marketing-image-section__hint">
                  | Imagen opcional para incluir en la difusión de la campaña
                </span>

                {form.imageUrl ? (
                  <div className="organizer-marketing-preview">
                    <img src={form.imageUrl} alt="Imagen promocional" />
                    <button
                      type="button"
                      className="organizer-marketing-preview__remove"
                      onClick={() => setForm((prev) => ({ ...prev, imageUrl: "" }))}
                      disabled={loading || uploadingImage}
                      aria-label="Quitar imagen"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                ) : (
                  <label
                    className={`organizer-marketing-upload-zone ${uploadingImage || loading ? "is-disabled" : ""} ${
                      isDragging ? "is-dragging" : ""
                    }`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      className="organizer-marketing-file-input"
                      onChange={onPickImage}
                      disabled={loading || uploadingImage}
                    />
                    <UploadCloudIcon />
                    <span className="organizer-marketing-upload-zone__label">
                      {uploadingImage ? "Subiendo imagen..." : isDragging ? "Soltá la imagen" : "Seleccionar archivo"}
                    </span>
                    <span className="organizer-marketing-upload-zone__hint">
                      {isDragging ? "Soltá el archivo acá para cargarlo" : "o arrastralo acá (Peso máximo 10 MB)"}
                    </span>
                  </label>
                )}
              </div>
            </div>

            <div className="organizer-marketing-email-foot">
              <span className="organizer-marketing-email-foot__counter">
                <strong>
                  {quotaRemaining != null && quotaLimit != null
                    ? `${formatNumber(quotaRemaining)}/${formatNumber(quotaLimit)}`
                    : "0/0"}
                </strong>{" "}
                Emails disponibles este mes
              </span>
              <div className="organizer-marketing-email-foot__actions">
                <button
                  type="button"
                  className="organizer-marketing-btn organizer-marketing-btn--ghost"
                  onClick={onTestEmail}
                  disabled={loading}
                >
                  {loading ? "Enviando..." : "Enviar email de prueba"}
                </button>
                <button
                  type="submit"
                  className="organizer-marketing-btn organizer-marketing-btn--primary"
                  disabled={loading || uploadingImage}
                >
                  {loading ? "Enviando..." : "Enviar email"}
                </button>
              </div>
            </div>
          </Form>
        </article>

        <article className="organizer-marketing-card organizer-marketing-history">
          <div className="organizer-marketing-history__head">
            <div>
              <h2 className="organizer-marketing-section-title">Últimas campañas</h2>
              <span className="organizer-marketing-history__sub">| Historial de envíos recientes</span>
            </div>
            <button
              type="button"
              className="organizer-marketing-btn organizer-marketing-btn--primary"
              onClick={load}
              disabled={loading}
            >
              {loading ? "Actualizando..." : "Actualizar"}
            </button>
          </div>

          <div className="organizer-marketing-table-wrap">
            <Table responsive className="organizer-marketing-table align-middle">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>ESTADO</th>
                  <th>ASUNTO</th>
                  <th style={{ width: 160 }}>SEGMENTO</th>
                  <th style={{ width: 140 }}>FECHA</th>
                  <th style={{ width: 140 }}>HORARIO</th>
                </tr>
              </thead>
              <tbody>
                {historyItems.map((campaign) => {
                  const visualState = getCampaignVisualState(campaign);
                  const { date, time } = formatCampaignDate(campaign);
                  return (
                    <tr key={campaign._id}>
                      <td>
                        <span className={`organizer-marketing-chip is-${visualState}`}>
                          <span className="dot" />
                          {getCampaignStatusLabel(campaign)}
                        </span>
                      </td>
                      <td className="organizer-marketing-table__subject">{campaign.subject || "-"}</td>
                      <td>{campaign?.filter?.lastEventId ? "Por evento" : "Todos"}</td>
                      <td>{date}</td>
                      <td>{time}</td>
                    </tr>
                  );
                })}
                {historyItems.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">
                      Todavía no hay campañas.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </article>
      </>
    );
  };

  if (loading && !summary && !campaigns.length && !events.length) return <SpinLoading />;

  return (
    <div className="organizer-page organizer-marketing-page">
      <section className="organizer-marketing-shell">
        <div className="organizer-marketing-tabs" role="tablist" aria-label="Canales de difusión">
          {MARKETING_TABS.map((tab, idx) => {
            const activeIdx = MARKETING_TABS.findIndex((t) => t.key === activeTab);
            const isActive = activeTab === tab.key;
            const isNear = !isActive && Math.abs(idx - activeIdx) === 1;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`organizer-marketing-tab ${isActive ? "is-active" : ""} ${
                  isNear ? "is-inactive-near" : ""
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {error ? (
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        ) : null}

        {status ? (
          <Alert variant="success" className="mb-0">
            {status}
          </Alert>
        ) : null}

        {activeTab === "pixel" ? renderPixelTab() : null}
        {activeTab === "email" ? renderEmailTab() : null}
        {activeTab === "whatsapp" ? <MarketingComingSoon /> : null}
        {activeTab === "push" ? <MarketingComingSoon /> : null}
      </section>
    </div>
  );
};

export default Marketing;
